"use client";

import type React from "react";

import { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Typography,
  Divider,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Paper,
  Stack,
  Grid,
  CircularProgress,
} from "@mui/material";
import {
  Send,
  AttachFile,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  ArrowDropDown,
  Close,
  Schedule,
  Save,
  Cancel,
} from "@mui/icons-material";
import { EditToggle, EditType } from "@/components/EditToggle";
import { apiRemote } from "@/api";
import { useAlert } from "@/contexts/AlertContext";

export default function SendMailComponent() {
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [priority, setPriority] = useState("normal");
  const [priorityAnchor, setPriorityAnchor] = useState<null | HTMLElement>(
    null
  );
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const alert = useAlert();

  const addAttachment = () => {
    const fileName = `attachment-${attachments.length + 1}.pdf`;
    setAttachments([...attachments, fileName]);
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handlePriorityClick = (event: React.MouseEvent<HTMLElement>) => {
    setPriorityAnchor(event.currentTarget);
  };

  const handlePriorityClose = () => {
    setPriorityAnchor(null);
  };

  const handlePrioritySelect = (newPriority: string) => {
    setPriority(newPriority);
    handlePriorityClose();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "error";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case "high":
        return "🔴 High Priority";
      case "low":
        return "🟢 Low Priority";
      default:
        return "🟡 Normal Priority";
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message == "") {
      alert.error("Message is required");
      return false;
    }
    setLoading(true);
    try {
      const data = await apiRemote.sendMailAlert({
        message: message,
        subject: subject
      })
      // alert("The email is being sent.")
      alert.info("The email is being sent.")
    } catch (error: any) {
      // alert(`Error: ${error.message}`)
      console.log(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", p: 3 }}>
      <Card elevation={3}>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Send />
              <Typography variant="h6">New Email</Typography>
            </Box>
          }
        />
        <CardContent>
          <form onSubmit={handleSend}>
            <Stack spacing={3}>
              <Divider />

              {/* Subject and Priority */}
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={1}>
                  <Typography variant="body2" sx={{ textAlign: "right" }}>
                    Subject:
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    placeholder="Enter email subject..."
                    variant="outlined"
                    size="small"
                    value={subject}
                    required
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </Grid>
              </Grid>

              <Divider />

              {/* Message Body */}
              <Box>
                <EditToggle
                  label="Message"
                  data={{
                    value: message || "",
                    type: EditType.EDITOR,
                  }}
                  isEdit={true}
                  required={true}
                  onChange={(value: string) => {
                    const trimmed = value?.trimStart() || "";
                    if (trimmed.length < 5000) {
                      setMessage(trimmed);
                    }
                  }}
                />
              </Box>

              <Divider />

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    startIcon={
                      loading ? <CircularProgress size={20} /> : <Send />
                    }
                    color="primary"
                    disabled={loading}
                    type="submit"
                  >
                    Send Email
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
